/**
 * Browser task detail page with live streaming and comprehensive task information
 */

import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Play, Pause, Square } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
// useQuery removed - using hooks from use-anchor-browser instead
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { LiveBrowserViewer, TaskStepsList, TaskMediaViewer, ErrorBoundary, TaskDetailSkeleton } from '../components/anchor-browser'
import { useTask, useTaskMedia, useStopTask, usePauseTask, useResumeTask } from '../hooks/use-anchor-browser'
import { useToast } from '../hooks/use-toast'
import type { TaskStatus } from '../types/anchor-browser'

function getStatusVariant(status: TaskStatus): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case 'queued':
      return 'outline' // Waiting to start
    case 'running':
      return 'default' // Actively executing
    case 'completed':
    case 'finished':
      return 'secondary' // Successfully completed
    case 'failed':
      return 'destructive' // Failed execution
    case 'paused':
    case 'stopped':
      return 'outline' // Paused/stopped
    default:
      return 'secondary'
  }
}

function BrowserTaskDetailContent() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const taskId = id!
  const { data: task, isLoading, error, refetch } = useTask(taskId)

  // Fetch task media
  const { data: mediaData, error: mediaError } = useTaskMedia(taskId)

  // Check if we have any media at all
  const hasAnyMedia = (mediaData?.screenshots?.length || 0) > 0 ||
                     (mediaData?.recordings?.length || 0) > 0 ||
                     !!mediaData?.gif

  // Determine loading state
  const isLoadingMedia = !mediaData && !mediaError && !!task
  
  const stopTaskMutation = useStopTask()
  const pauseTaskMutation = usePauseTask()
  const resumeTaskMutation = useResumeTask()

  const handleTaskAction = async (action: 'stop' | 'pause' | 'resume') => {
    try {
      switch (action) {
        case 'stop':
          await stopTaskMutation.mutateAsync(taskId)
          toast({ title: 'Task Stopped', description: 'Task has been stopped successfully.' })
          break
        case 'pause':
          await pauseTaskMutation.mutateAsync(taskId)
          toast({ title: 'Task Paused', description: 'Task has been paused successfully.' })
          break
        case 'resume':
          await resumeTaskMutation.mutateAsync(taskId)
          toast({ title: 'Task Resumed', description: 'Task has been resumed successfully.' })
          break
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${action} task. Please try again.`,
        variant: 'destructive',
      })
    }
  }

  // File downloads not supported in Anchor Browser

  if (isLoading) {
    return <TaskDetailSkeleton />
  }

  if (error || !task) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/browser-tasks')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Task Not Found</h1>
        </div>
        <Alert variant="destructive">
          <AlertDescription>
            The requested task could not be found or failed to load.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const canStop = task.status === 'running' || task.status === 'paused'
  const canPause = task.status === 'running'
  const canResume = task.status === 'paused'
  const isActionLoading = stopTaskMutation.isPending || pauseTaskMutation.isPending || resumeTaskMutation.isPending

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/browser-tasks')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Task Details</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={getStatusVariant(task.status)}>
                {task.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                ID: {task.id}
              </span>
              {/* Real-time connection status */}
              {(task.status === 'queued' || task.status === 'running') && (
                <div className="flex items-center gap-1 text-blue-600">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span className="text-xs">
                    {task.status === 'queued' ? 'Waiting to start...' : 'Live updates active'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
          >
            Refresh
          </Button>

          {canResume && (
            <Button onClick={() => handleTaskAction('resume')} disabled={isActionLoading}>
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
          )}

          {canPause && (
            <Button variant="outline" onClick={() => handleTaskAction('pause')} disabled={isActionLoading}>
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
          )}

          {canStop && (
            <Button variant="destructive" onClick={() => handleTaskAction('stop')} disabled={isActionLoading}>
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          )}
        </div>
      </div>



      {/* Task Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Task Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2">Description</h4>
            <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
              {task.task}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Created</span>
              <p className="font-medium">
                {task.created_at ? formatDistanceToNow(new Date(task.created_at), { addSuffix: true }) : 'Unknown'}
              </p>
            </div>
            {task.finished_at && (
              <div>
                <span className="text-muted-foreground">Finished</span>
                <p className="font-medium">
                  {formatDistanceToNow(new Date(task.finished_at), { addSuffix: true })}
                </p>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Steps</span>
              <p className="font-medium">{task.steps?.length || 0}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status</span>
              <p className="font-medium capitalize">{task.status}</p>
            </div>
          </div>

          {task.output && (
            <div>
              <h4 className="text-sm font-medium mb-2">Output</h4>
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                {task.output}
              </p>
            </div>
          )}

          {/* Links */}
          <div className="flex flex-wrap gap-4">
            {task.public_share_url && (
              <a
                href={task.public_share_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Public Share
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="live" className="space-y-4">
        <TabsList>
          <TabsTrigger value="live">Live View</TabsTrigger>
          <TabsTrigger value="steps">Execution Steps</TabsTrigger>
          <TabsTrigger value="files">Files & Media</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="space-y-4">
          <LiveBrowserViewer
            liveUrl={task.live_view_url || task.provider_data?.live_view_url || task.live_url}
            taskId={task.id}
            taskStatus={task.status}
          />
        </TabsContent>

        <TabsContent value="steps" className="space-y-4">
          <TaskStepsList
            steps={task.steps || []}
            currentStep={task.status === 'running' && task.steps ? task.steps.length : undefined}
          />
        </TabsContent>

        <TabsContent value="files" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* User Uploaded Files - Not supported in Anchor Browser */}
            {false && (
              <Card>
                <CardHeader>
                  <CardTitle>Uploaded Files</CardTitle>
                  <CardDescription>Files provided for this task</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">File uploads not supported in Anchor Browser</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* File uploads/downloads not supported in Anchor Browser */}

            {/* Media Files */}
            <div className="md:col-span-2">
              <TaskMediaViewer
                media={mediaData}
                isLoading={isLoadingMedia}
                error={mediaError}
                taskStatus={task?.status}
              />
            </div>
          </div>

          {!hasAnyMedia && (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground">No media files available for this task</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export function BrowserTaskDetail() {
  return (
    <ErrorBoundary>
      <BrowserTaskDetailContent />
    </ErrorBoundary>
  )
}
